import ApiService from "@/core/services/api.service";
import JwtService from "@/core/services/jwt.service";
import {SET_SNACK} from "@/core/services/store/snackbar.module";
import router from "../../../router";

// action types
export const VERIFY_AUTH = "verifyAuth";
export const LOGIN = "login";
export const RESEND_LOGIN_CODE = "resendLoginCode";
export const FETCH_LOGIN_DEVICES = "fetchLoginDevices";
export const REMOVE_LOGIN_DEVICE = "removeLoginDevice";
export const GHOST_LOGIN = "ghost_login";
export const REGISTER = "register";
export const FORGOT_PASSWORD = "forgot_password";
export const RESET_PASSWORD = "reset_password";
export const SEND_VERIFICATION_EMAIL = "send_verification_email";
export const LOGOUT = "logout";
export const DO_LOGOUT = "doLogout";
export const CHANGE_PASSWORD = "changePassword";
export const CLEAR_AUTH_ERRORS = "clearAuthErrors";

// mutation types
export const PURGE_AUTH = "logOut";
export const SET_AUTH = "setUser";
export const SET_AUTH_ERRORS = "setAuthError";
export const REMOVE_AUTH_ERRORS = "removeAuthErrors";

const state = {
    errors: [],
    user: {},
    vendor: '',
    c_d: '',
    vendor_email: '',
    t_id: '',
    roles: [],
    permissions: [],
    isAuthenticated: !!JwtService.getToken()
};

const getters = {
    currentUser(state) {
        return state.user;
    },
    isAuthenticated(state) {
        return state.isAuthenticated;
    }
};

const MAINTENANCE_LOGIN_ERROR = [{ map: "maintenance", message: "System is under maintenance" }];

function isDefinitiveAuthFailure(error) {
    const response = error && error.response ? error.response : error;
    const status = response && response.status;
    const outcome = response && response.data && response.data._metadata
        ? response.data._metadata.outcomeCode
        : null;

    return status === 401 || outcome === 1;
}

function authErrorsFromAxiosError(error) {
    const response = error && error.response ? error.response : null;
    let errors = response?.data?.errors || response?.errors || [];

    if ((!Array.isArray(errors) || errors.length === 0) && response?.status === 503) {
        return MAINTENANCE_LOGIN_ERROR;
    }

    if (Array.isArray(errors) && errors.length && typeof errors[0] === "string") {
        return errors.map((message) => ({ message }));
    }

    return Array.isArray(errors) ? errors : [];
}

const actions = {
    [LOGIN](context, credentials) {
        return new Promise((resolve, reject) => {
            ApiService.post("api/auth/login", credentials)
                .then(({data}) => {
                    if (data._metadata.outcomeCode === 0) {
                        context.commit(SET_AUTH, data);
                        resolve(data);
                    } else if (data._metadata.outcomeCode === 10) {
                        resolve(data);
                    } else {
                        resolve(data);
                        context.commit(SET_AUTH_ERRORS, data.errors);
                    }
                })
                .catch((error) => {
                    context.commit(SET_AUTH_ERRORS, authErrorsFromAxiosError(error));
                    reject(error?.response || error);
                });
        });
    },
    [FETCH_LOGIN_DEVICES](context) {
        return new Promise((resolve, reject) => {
            ApiService.simple("api/auth/login-devices")
                .then(({data}) => {
                    if (data._metadata.outcomeCode === 0) {
                        resolve(data);
                    } else {
                        reject(data);
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },
    [REMOVE_LOGIN_DEVICE](context, deviceId) {
        return new Promise((resolve, reject) => {
            ApiService.post(`api/auth/login-devices/remove/${deviceId}`, {})
                .then(({data}) => {
                    if (data._metadata.outcomeCode === 0) {
                        resolve(data);
                    } else {
                        reject(data);
                    }
                })
                .catch((error) => {
                    reject(error);
                });
        });
    },
    [RESEND_LOGIN_CODE](context, credentials) {
        return new Promise((resolve, reject) => {
            ApiService.post("api/auth/resend-login-code", credentials)
                .then(({data}) => {
                    if (data._metadata.outcomeCode === 0) {
                        resolve(data);
                    } else {
                        context.commit(SET_AUTH_ERRORS, data.errors);
                        resolve(data);
                    }
                })
                .catch((error) => {
                    context.commit(SET_AUTH_ERRORS, authErrorsFromAxiosError(error));
                    reject(error?.response || error);
                });
        });
    },
    [GHOST_LOGIN](context, credentials) {
        return new Promise(resolve => {
            ApiService.post("api/auth/ghostlogin", credentials)
                .then(({data}) => {
                    if (data._metadata.outcomeCode === 0) {
                        context.commit(SET_AUTH, data);
                        resolve(data);
                    } else {
                        resolve(data);
                        context.commit(SET_AUTH_ERRORS, data.errors);
                    }
                })
                .catch((error) => {
                    context.commit(SET_AUTH_ERRORS, authErrorsFromAxiosError(error));
                });
        });
    },
    [REGISTER](context, credentials) {
        return new Promise(resolve => {
            ApiService.post("api/auth/signup", credentials)
                .then(({data}) => {
                    if (data._metadata.outcomeCode === 0) {
                        resolve(data);
                    } else {
                        resolve(data);
                        context.commit(SET_AUTH_ERRORS, data.errors);
                    }
                })
                .catch((error) => {
                    context.commit(SET_AUTH_ERRORS, authErrorsFromAxiosError(error));
                });
        });
    },
    [FORGOT_PASSWORD](context, credentials) {
        return new Promise(resolve => {
            ApiService.post("api/auth/resetemail", credentials)
                .then(({data}) => {
                    if (data._metadata.outcomeCode === 0) {
                        resolve(data);
                    } else {
                        resolve(data);
                        context.commit(SET_AUTH_ERRORS, data.errors);
                    }
                })
                .catch((error) => {
                    context.commit(SET_AUTH_ERRORS, authErrorsFromAxiosError(error));
                });
        });
    },
    [RESET_PASSWORD](context, credentials) {
        return new Promise(resolve => {
            ApiService.post("api/auth/resetpassword", credentials)
                .then(({data}) => {
                    if (data._metadata.outcomeCode === 0) {
                        resolve(data);
                    } else {
                        resolve(data);
                        context.commit(SET_AUTH_ERRORS, data.errors);
                    }
                })
                .catch((error) => {
                    context.commit(SET_AUTH_ERRORS, authErrorsFromAxiosError(error));
                });
        });
    },
    [SEND_VERIFICATION_EMAIL](context, payload) {
        return new Promise(resolve => {
            ApiService.get("api/auth/email/resend", payload.email)
                .then(({data}) => {
                    if (data._metadata.outcomeCode === 0) {
                        resolve(data);
                    } else {
                        resolve(data);
                        context.commit(SET_AUTH_ERRORS, data.errors);
                    }
                })
                .catch((error) => {
                    context.commit(SET_AUTH_ERRORS, authErrorsFromAxiosError(error));
                });
        });
    },
    [DO_LOGOUT](context) {
        ApiService.post("api/auth/logout")
            .then(({data}) => {
                if (data._metadata.outcomeCode === 0) {
                    this.dispatch(SET_SNACK, {clr: 'success', msg: data._metadata.message});
                    context.commit(PURGE_AUTH);
                    router.push({name: "login"})
                } else {
                    this.dispatch(SET_SNACK, {clr: 'error', msg: data._metadata.message});
                    context.commit(SET_AUTH_ERRORS, data.errors);
                }
            })
            .catch((error) => {
                context.commit(SET_AUTH_ERRORS, authErrorsFromAxiosError(error));
            });
    },
    [LOGOUT](context) {
        context.commit(PURGE_AUTH);
        router.push({name: "login"})
    },
    [CHANGE_PASSWORD](context, data) {
        if (JwtService.getToken()) {
            ApiService.setHeader();
            return new Promise((resolve, reject) => {
                ApiService.post("api/auth/changepassword", data)
                    .then(({data}) => {
                        if (data._metadata.outcomeCode === 0) {
                            this.dispatch(SET_SNACK, {clr: 'success', msg: data._metadata.message});
                            resolve(data);
                        } else {
                            this.dispatch(SET_SNACK, {clr: 'error', msg: data._metadata.message});
                            context.commit(SET_AUTH_ERRORS, data.errors);
                        }
                    })
                    .catch(({response}) => {
                        context.commit(SET_AUTH_ERRORS, response.errors);
                        reject(response);
                    });
            });
        } else {
            context.commit(PURGE_AUTH);
        }
    },
    [VERIFY_AUTH](context, to) {
        if (!JwtService.getToken()) {
            context.commit(PURGE_AUTH);
            return Promise.resolve(null);
        }

        ApiService.setHeader();
        return ApiService.doVerify("api/auth/verify", { to: to })
            .then(({ data }) => {
                if (data && data._metadata && data._metadata.outcomeCode === 0) {
                    context.commit(SET_AUTH, data);
                    return data;
                }
                context.commit(PURGE_AUTH);
                return data || null;
            })
            .catch((error) => {
                if (isDefinitiveAuthFailure(error)) {
                    context.commit(PURGE_AUTH);
                }
                return Promise.reject(error);
            });
    },
    [CLEAR_AUTH_ERRORS](context) {
        context.commit(REMOVE_AUTH_ERRORS);
    },
};

const mutations = {
    [SET_AUTH_ERRORS](state, error) {
        state.errors = Array.isArray(error) ? error : [];
    },
    [REMOVE_AUTH_ERRORS](state) {
        state.errors = [];
    },
    [SET_AUTH](state, data) {
        // let uRoles = [];
        let uPermissions = [];
        const records = data && data.records ? data.records : {};
        const permissions = Array.isArray(records.permissions) ? records.permissions : [];
        if (permissions.length !== 0) {
            permissions.forEach(function(v) {
                if (v && v.name) {
                    uPermissions.push(v.name);
                }
                // if (v.role_permissions && v.role_permissions.length !== 0) {
                //     v.role_permissions.forEach(function(vv) {
                //         uPermissions.push(vv.name);
                //     });
                // }
                // console.log(k.name);
            });
        }
        state.isAuthenticated = true;
        state.user = records.user || {};
        state.vendor = records.vendor;
        state.vendor_email = records.vendor_email;
        state.t_id = records.t_id;
        state.c_d = records.c_d;
        // state.roles = uRoles;
        state.permissions = uPermissions;
        state.errors = [];
        if (records.token) {
            JwtService.saveToken(records.token);
        }
        ApiService.setHeader();
    },
    [PURGE_AUTH](state) {
        state.isAuthenticated = false;
        state.user = {};
        state.vendor = '';
        state.vendor_email = '';
        state.t_id = '';
        state.c_d = '';
        state.errors = [];
        JwtService.destroyToken();
        ApiService.setHeader();
    }
};

export default {
    state,
    actions,
    mutations,
    getters
};
